<template>
  <div class="cont dc-js-chart">
    <h2 v-if="loading" class="loading">
      Loading data...
    </h2>
    <div class="fluid">
      <v-layout>
        <v-flex xs7>
          <RegionTimeChart
            ref="regionTimeChart"
            dimension-field="region"
            agg-field="value"
            :ndx="$options.ndx"
            :interval="interval"
            :download-name="downloadName"
            @dimensionSelected="filterRegionTime"
          >
            <div class="select-chart">
              <div class="selected">
                <h2 @click.stop="showChartTypes = !showChartTypes">
                  <i class="material-icons">{{ showChartTypes ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</i>
                  {{ selectedChart.title }}
                </h2>
                <div class="sub">
                  <strong>{{ selectedChart.unit }}</strong>
                  <a @click="resetAll()" class="reset-all">Reset all</a>
                </div>
              </div>

              <ul v-if="showChartTypes">
                <li
                  v-for="chart in $options.chartTypes"
                  :key="chart.title"
                  :class="{ selected: chart.title === selectedChart.title }"
                  @click="changeData(chart)"
                >
                  {{ chart.title }}
                </li>
              </ul>
            </div>
          </RegionTimeChart>

          <ChartForm
            ref="form" @formChanged="reloadChart()"
            :minDate="$options.minDate"
            :maxDate="$options.maxDate"
            :ndx="$options.ndx"
            :ndxLoaded="ndxLoaded"
          />

          <v-layout>
            <v-flex xs4>
              <PieChart
                ref="componentChart"
                dimension-field="type"
                agg-field="value"
                :colors="$options.colors"
                :pie-attrs="$options.pieAttrs"
                download-name="units_by_type.csv"
                :download-columns="['Type', 'Units']"
                :labelFn="labelFn"
                :titleFn="labelFn"
              >
                <h2>{{ selectedChart.unit }} by Type</h2>
                <div class="sub">
                  <strong class="sub">({{ minYear }} - {{ maxYear }}) {{ selectedChart.unit }}</strong>
                </div>
              </PieChart>
            </v-flex>
            <v-flex xs4>
              <PieChart
                ref="statusChart"
                dimension-field="status"
                agg-field="value"
                :colors="$options.colors"
                :pie-attrs="$options.pieAttrs"
                download-name="units_by_status.csv"
                :download-columns="['Status', 'Units']"
                :labelFn="labelFn"
                :titleFn="labelFn"
              >
                <h2>{{ selectedChart.unit }} by Status</h2>
                <div class="sub">
                  <strong class="sub">({{ minYear }} - {{ maxYear }}) {{ selectedChart.unit }}</strong>
                </div>
              </PieChart>
            </v-flex>
            <v-flex xs4></v-flex>
          </v-layout>
        </v-flex>

        <v-flex xs5>
          <v-layout>
            <v-flex xs6>
              <RowChart
                ref="operatorsChart"
                dimension-field="operator"
                :colors="$options.colors"
                agg-field="value"
                download-name="activity_by_E&P_units.csv"
                :download-columns="['Company', 'Units']"
                :labelFn="labelFn"
                :titleFn="labelFn"
              >
                <h2>Activity by E&P</h2>
                <div class="sub">
                  <strong class="sub">{{ selectedChart.unit }}</strong>
                </div>
              </RowChart>
            </v-flex>

            <v-flex xs6>
              <RowChart
                ref="countriesChart"
                dimension-field="country"
                agg-field="value"
                download-name="activity_by_country_units.csv"
                :download-columns="['Country', 'Units']"
                :colors="$options.colors"
                :labelFn="labelFn"
                :titleFn="labelFn"
              >
                <h2>Activity by Country</h2>
                <div class="sub">
                  <strong class="sub">{{ selectedChart.unit }}</strong>
                </div>
              </RowChart>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
import crossfilter from 'crossfilter2';
import '@node_modules/dc/dc.css';

import RegionTimeChart from '@/components/dcjs/RegionTime.vue';
import RowChart from '@/components/dcjs/RowChart.vue';
import PieChart from '@/components/dcjs/PieChart.vue';
import ChartForm from '@/components/dcjs/Form.vue';
import baseMixin from '@/components/dcjs/baseMixin';

export default {
  mixins: [baseMixin],
  chartTypes: [
    {
      title: 'Subsea Production Equipment',
      unit: 'Units',
      field: 'units',
      filters: ['Tree', 'Jumper', 'Manifold / Templates', 'Flying Leads', 'SubseaProcessing'].map(d => {
        return { type: d };
      }),
    },
    {
      title: 'SURF Equipment ',
      unit: 'KM',
      field: 'kilometers',
      filters: [
        { type: 'Flowline', subtype: 'Rigid' },
        { type: 'Flowline', subtype: 'Flexible' },
        { type: 'Umbilical', subtype: 'Subsea Power (SPU)' },
        { type: 'Umbilical', subtype: 'Thermoplastic (TPU)' },
        { type: 'Riser' },
      ],
    },
    {
      title: 'Pipelines Equipment ',
      unit: 'KM',
      field: 'kilometers',
      filters: [{ type: 'Export Pipeline' }, { type: 'Trunk' }, { type: 'Flowline', subtype: 'Rigid' }],
    },
  ],
  years: [],
  originalData: [],
  components: {
    RegionTimeChart,
    PieChart,
    RowChart,
    ChartForm,
  },
  computed: {
    downloadName() {
      return `${this.selectedChart.title
        .trim()
        .toLowerCase()
        .replace(/\s/g, '_')}.csv`;
    },
    labelFn() {
      return d => {
        return `${d.key}: ${d3.format(',.1f')(d.value)}`;
      };
    },
  },
  //
  data() {
    return {
      comp: {},
      region: {},
      years: [],
      period: null,
      interval: null,
      ndxLoaded: false,
      showChartTypes: false,
      loading: false,
      selectedChart: this.$options.chartTypes[0],
    };
  },
  methods: {
    changeData(chart) {
      this.selectedChart = chart;
      this.reloadChart();
    },
    async $_loadData() {
      let data = [];
      this.loading = true;
      try {
        data = await d3.csv('/subsea-data.csv');
      } catch (err) {
        alert('There was an error fetching data from the server, please reload the page?');
        this.loading = false;
        return;
      }
      this.$options.originalData = [...data];
      this.loading = false;
      return data;
    },
    $_getData(data) {
      const minDate = new Date(this.$options.minDate);
      const maxDate = new Date(this.$options.maxDate);

      return data
        .map(d => {
          const r = {
            capex: +(d.capex || 0) / 1000,
            country: d.country || '',
            _type: d._type,
            subtype: d.subtype,
            type: this.$_getType(d),
            date: new Date(`${d[this.period]}T00:00:00`),
            operator: d.operator || '',
            region: d.region || '',
            status: d.status,
            water_depth: +(d.water_depth_m || 0),
            kilometers: +d.kilometers || 0,
            units: +d.units || 0,
          };
          return { ...r, value: r[this.selectedChart.field] };
        })
        .filter(d => d.date != 'Invalid Date')
        .filter(d => d.date > minDate && d.date < maxDate)
        .filter(d => d.type);
    },
    $_getType(d) {
      const typ = this.selectedChart.filters.find(v => v.type === d._type);

      if (typ && typ.subtype) {
        return typ.subtype;
      } else if (typ) {
        return typ.type;
      } else {
        return null;
      }
    },
    //
    $_createDashboard(data) {
      this.years = [...new Set(data.map(d => d.date.getFullYear()))].sort();
      const ndx = crossfilter(data);
      this.$options.ndx = ndx;
      this.ndxLoaded = true;

      this.$refs.componentChart.createChart(ndx);
      this.$refs.statusChart.createChart(ndx);
      this.$refs.operatorsChart.createChart(ndx);
      this.$refs.countriesChart.createChart(ndx);
      this.$refs.regionTimeChart.createChart(ndx);

      dc.renderAll();
    },
    //
    $_selectStack(i) {
      return d => d.value[i];
    },
  },
  async mounted() {
    const data = await this.$_loadData();
    this.period = this.$refs.form.form.period;
    this.interval = this.$refs.form.form.interval;
    this.$_createDashboard(this.$_getData(data));

    document.body.addEventListener('click', () => {
      this.showChartTypes = false;
    });
    // Listen filters
    this.charts.forEach(chart => {
      this.$refs[chart].chart.on('filtered', () => {
        setTimeout(() => {
          this.$refs.regionTimeChart.updateLabels();
        }, 100);
      });
    })
  },
};
</script>

<style>
.dc-chart .selected {
  cursor: pointer;
}
.dc-chart .select-chart .selected i {
  vertical-align: bottom;
  font-size: 24px;
  background: #1b365d;
  color: white;
  border-radius: 3px;
  border: 1px solid #6a6e72;
}
.dc-chart .select-chart .selected i:hover {
  color: white;
}
.dc-chart .select-chart ul {
  list-style: none;
  position: absolute;
  top: 32px;
  width: 300px;
  background: white;
  border: 1px solid #cfcfcf;
  padding: 0px;
  margin: 0px;
  z-index: 100;
}
.dc-chart .select-chart ul li {
  padding: 4px 10px;
  margin: 0px;
  cursor: pointer;
}
.dc-chart .select-chart ul li.selected {
  font-weight: bold;
}
.dc-chart .select-chart ul li:hover {
  background: #8bc1ff;
}
</style>
